import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { createPasswordSchema, type PasswordFormData } from '../../lib/validators';
import {
  useLocalizedSchema,
  useRevalidateOnLanguageChange,
} from '../../hooks/useLocalizedSchema';
import { resetPasswordApi } from '../../services/api';
import { getErrorMessage, extractApiError } from '../../lib/errorCodes';
import { PasswordField, Button } from '../../components/ui/Form';
import { PasswordRules } from '../../components/ui/PasswordRules';
import './ResetPasswordPage.css';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ id: string; token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const passwordSchema = useLocalizedSchema(createPasswordSchema);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  useRevalidateOnLanguageChange(trigger, isSubmitted);

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const onSubmit = async (data: PasswordFormData) => {
    if (!token) return;
    setIsLoading(true);
    try {
      const result = await resetPasswordApi(token, data.password);
      if (result.errorCode) {
        toast.error(getErrorMessage(result.errorCode, 'apiErrors.invalidToken'));
      } else {
        toast.success(t('general.passwordReset'));
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="auth-reset__header">
        <h1 className="auth-reset__title">{t('auth.resetTitle')}</h1>
        <p className="auth-reset__subtitle">{t('auth.resetSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="auth-reset__form" noValidate>
        <PasswordField
          label={t('general.passwordLabel')}
          placeholder="••••••••••••"
          autoComplete="new-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordField
          label={t('general.confirmPassword')}
          placeholder="••••••••••••"
          autoComplete="new-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        {password.length > 0 && (
          <PasswordRules password={password} confirmPassword={confirmPassword} />
        )}

        <Button type="submit" isLoading={isLoading} withArrow>
          {t('general.resetButton')}
        </Button>
      </form>

      <div className="auth-reset__footer">
        <button type="button" className="auth-link" onClick={() => navigate('/')}>
          {t('general.backToLogin')}
        </button>
      </div>
    </>
  );
}
